// Third party
import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { CardElement, CardElementChangeEvent, useRecurly } from '@recurly/react-recurly';

// Internal libs
import Button from '@fff-web/fff-ui-shared/Button';
import Input from '@fff-web/fff-ui-shared/Input';
import PaymentMethod from '@fff-web/fff-ui-shared/PaymentMethod';
import Select from '@fff-web/fff-ui-shared/Select';
import type { BillingInformationType } from '@fff-web/fff-ui-shared/PaymentMethod/interfaces';

// Internal modules
import { processCustomBillingInfo } from './utils';
import useFormData from './useFormData';
import { useBillingInfo } from '../../../../lib/ContextProviders';
import * as constants from './constants';
import type {
  FormRowProps,
  BillingInfoFormProps,
  UpdatePaymentFormV2Props,
  FormData,
  FooterProps,
  CheckboxFormRowProps,
} from './types';
import { handleRecurlyError } from './recurlyErrorHandler';

// Styles
import styles from './UpdatePaymentFormV2.module.css';

const UpdatePaymentFormV2 = ({ onSuccess, onClose }: UpdatePaymentFormV2Props) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCreditValid, setIsCreditValid] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const recurly = useRecurly();
  const { billingInfo, handleSubmit: updateBillingInfo } = useBillingInfo();

  const initialFormData: FormData = {
    fullName: `${billingInfo?.firstName} ${billingInfo?.lastName}`,
    country: billingInfo?.country || '',
    postal_code: billingInfo?.zip || '',
    billingInfoSameAsShipping: true,
  };

  const {
    formData,
    handleInputChange,
    handleErrors,
    formErrors,
    removeFormError,
    setFormData,
    handleCheckbox,
    handleBlur,
    handleCountryChange,
  } = useFormData<FormData>(initialFormData);

  useEffect(() => {
    setFormData(initialFormData);
  }, [billingInfo]);

  const isDisabled = Object.keys(formErrors).length > 0 || !isCreditValid || isSubmitting;

  const handleRecurlyCardChange = (e: CardElementChangeEvent) => {
    if (e.valid !== isCreditValid) {
      setIsCreditValid(e.valid);
    }
    const isInvalid = !e.focus && !e.empty && !e.valid;
    isInvalid
      ? handleErrors({ creditCard: 'Please enter valid credit card information.' })
      : removeFormError('creditCard');
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!formRef.current || Object.keys(formErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);

    try {
      const formElement = formRef.current;
      const token = await processCustomBillingInfo({
        formElement,
        formData,
        recurly,
        onError: (error) => handleRecurlyError(error, handleErrors, setFormData),
      });

      await updateBillingInfo({
        id: token.id,
        type: token.type,
      });

      onSuccess();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!billingInfo) {
    return null;
  }

  return (
    <form ref={formRef} onSubmit={handleSubmit} className={styles.form}>
      <div className={styles.paymentMethod}>
        <PaymentMethod billingInformation={billingInfo as BillingInformationType} />
      </div>
      <FormRow label="Credit Card" className={styles.recurlyFormRow}>
        <CardElement style={constants.CARD_INPUT_STYLES} onChange={handleRecurlyCardChange} />
        {formErrors.creditCard && <span className={styles.error}>{formErrors.creditCard}</span>}
      </FormRow>
      <CheckboxFormRow
        billingInfoSameAsShipping={formData.billingInfoSameAsShipping}
        toggleBillingInfoSameAsShipping={handleCheckbox}
      />
      {!formData.billingInfoSameAsShipping && (
        <BillingInfoForm
          handleInputChange={handleInputChange}
          formData={formData}
          formErrors={formErrors}
          handleBlur={handleBlur}
          handleCountryChange={handleCountryChange}
        />
      )}
      <Footer isSubmitting={isSubmitting} isDisabled={isDisabled} onClose={onClose} />
    </form>
  );
};

const FormRow = ({ children, label, className }: FormRowProps) => (
  <div className={classNames(styles.formRow, className)}>
    {label && <label>{label}</label>}
    {children}
  </div>
);

const CheckboxFormRow = ({
  billingInfoSameAsShipping,
  toggleBillingInfoSameAsShipping,
}: CheckboxFormRowProps) => {
  return (
    <div className={styles.checkboxFormRow}>
      <input
        type="checkbox"
        id={constants.CHECKBOX_INPUT.name}
        name={constants.CHECKBOX_INPUT.name}
        checked={billingInfoSameAsShipping}
        onChange={toggleBillingInfoSameAsShipping}
      />
      <label htmlFor={constants.CHECKBOX_INPUT.name} className={styles.checkboxLabel}>
        {constants.CHECKBOX_INPUT.label}
      </label>
    </div>
  );
};

const BillingInfoForm = ({
  handleInputChange,
  formData,
  formErrors,
  handleBlur,
  handleCountryChange,
}: BillingInfoFormProps) => {
  return (
    <div>
      <div className={styles.billingInfoHeader}>
        <h3>Billing Info</h3>
      </div>
      <FormRow>
        <Input
          id="fullName"
          name="fullName"
          label="Name on Card"
          type="text"
          onChange={handleInputChange}
          onBlur={handleBlur}
          className={styles.input}
          value={formData.fullName}
          error={formErrors.fullName || undefined}
          containerClassName={styles.inputContainer}
        />
      </FormRow>
      <div className={styles.doubleInputContainer}>
        <FormRow>
          <Select
            name="country"
            label="Country"
            onChange={handleCountryChange}
            list={constants.COUNTRIES}
            selectClassName={styles.select}
            containerClassname={styles.selectContainer}
            dataRecurly="country"
            value={formData.country}
          />
        </FormRow>
        <FormRow>
          <Input
            id="postal_code"
            name="postal_code"
            label="Zip/Postal Code"
            onChange={handleInputChange}
            onBlur={handleBlur}
            value={formData.postal_code}
            className={classNames(styles.input, styles.zipCodeInput)}
            containerClassName={styles.inputContainer}
            dataRecurly="postal_code"
            error={formErrors.postal_code || undefined}
            type="text"
          />
        </FormRow>
      </div>
    </div>
  );
};

const Footer = ({ isSubmitting, isDisabled, onClose }: FooterProps) => (
  <div className={styles.footer}>
    <Button variation="black-outline" type="button" onClick={onClose}>
      Cancel
    </Button>
    <Button
      variation="black"
      type="submit"
      className={styles.updateButton}
      disabled={isSubmitting || isDisabled}
      loading={isSubmitting}
    >
      <div className={styles.buttonContent}>
        <i className="fas fa-lock" />
        <span>Update</span>
      </div>
    </Button>
  </div>
);

export default UpdatePaymentFormV2;
